<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-3xl">
      <div class="flex items-center space-x-4 mb-6">
        <button @click="goBack" class="p-2 hover:bg-gray-100 rounded-lg">
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 class="text-2xl font-bold text-gray-900">Final Verification</h1>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 class="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">History</h2>
        <div v-if="verifications.length" class="space-y-3">
          <div
            v-for="v in verifications"
            :key="v.id"
            class="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3"
          >
            <div>
              <p class="text-sm font-medium text-gray-900">{{ v.final_stage_label }}</p>
              <p class="text-sm text-gray-600">{{ v.summary || "No summary provided" }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ formatDate(v.created_at) }} — {{ v.user?.name }}</p>
            </div>
            <StatusBadge :status="v.decision_label" />
          </div>
        </div>
        <p v-else class="text-gray-500 text-sm">No final verification submitted yet.</p>
      </div>

      <!-- Prior Stage Status -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 class="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Prior Verification Stages</h2>
        <div class="space-y-2">
          <div
            v-for="stage in completeness.stages"
            :key="stage.label"
            class="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2"
          >
            <span class="text-sm text-gray-900">{{ stage.label }}</span>
            <span
              class="text-xs font-medium px-2 py-1 rounded-md"
              :class="stageBadgeClass(stage.status)"
            >
              {{ stageLabel(stage.status) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Identity Verification -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 class="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Identity Verification</h2>

        <div v-if="ticketRejected" class="text-sm text-red-600">
          This ticket has already been rejected — identity verification and final approval are no longer available.
        </div>

        <div v-else-if="!identityVerification">
          <p v-if="!completeness.complete" class="text-sm text-gray-500 mb-3">
            Available once all prior verification stages are complete.
          </p>
          <p v-else-if="completeness.has_rejection" class="text-sm text-red-600 mb-3">
            One or more prior verification stages was rejected. Identity verification cannot be run — this ticket should be rejected at Final Verification instead.
          </p>
          <button
            v-else
            @click="openIdentityConfirm"
            :disabled="isRunningIdentityCheck"
            class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50"
          >
            {{ isRunningIdentityCheck ? "Running check..." : "Run Identity Verification" }}
          </button>
        </div>

       <div v-else class="space-y-3">
  <div class="flex items-center justify-between">
    <span class="text-sm font-medium text-gray-900">Overall Status</span>
    <StatusBadge :status="identityVerification.overall_status" />
  </div>
  <div class="grid grid-cols-2 gap-3 text-sm">
    <div><span class="text-gray-500">ID Number Match:</span> {{ identityVerification.id_number_match ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">Kenyan ID:</span> {{ identityVerification.is_kenyan_id ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">Maisha Card:</span> {{ identityVerification.is_maisha_card ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">Serial Number:</span> {{ identityVerification.serial_number || "N/A" }}</div>
    <div><span class="text-gray-500">Face Match:</span> {{ identityVerification.face_match ? "Yes" : "No" }} ({{ identityVerification.face_match_score }}%)</div>
    <div v-if="identityVerification.face_match_inconclusive" class="text-orange-600">
      Inconclusive: {{ identityVerification.face_match_reason }}
    </div>
    <div><span class="text-gray-500">IPRS Found:</span> {{ identityVerification.iprs_found ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">Hakikisha Matched:</span> {{ identityVerification.hakikisha_matched ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">MRZ Valid:</span> {{ identityVerification.mrz_valid ? "Yes" : "No" }}</div>
    <div><span class="text-gray-500">Selfie Quality:</span> {{ identityVerification.selfie_quality_score }}</div>
  </div>
  <div v-if="identityVerification.decision_reasons?.length" class="text-sm text-red-600">
    Reasons: {{ identityVerification.decision_reasons.join(", ") }}
  </div>

  <label class="flex items-center gap-2 mt-3 text-sm text-gray-700">
    <input type="checkbox" v-model="identityConfirmed" />
    I've reviewed this identity verification result and confirm it's accurate.
  </label>
</div>
      </div>

      <div v-if="!completeness.complete" class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
        Final verification cannot be approved yet — the following stages are still incomplete:
        <strong>{{ completeness.incomplete.join(", ") }}</strong>. You can still submit a rejection.
      </div>
      <div v-else-if="completeness.has_rejection" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
        One or more prior verification stages were rejected. Final verification will be automatically set to <strong>Rejected</strong>, regardless of the decision selected below.
      </div>

      <button
        v-if="!ticketRejected"
        @click="openModal"
        :disabled="!identityVerification || !identityConfirmed"
        class="w-full flex items-center justify-center px-4 py-3 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Submit Final Verification
      </button>
      <div v-else class="text-sm text-gray-500 italic px-4 py-3 text-center">
        This ticket has already been rejected at final verification.
      </div>
    </div>

    <!-- Identity Verification Confirmation Modal -->
    <div v-if="showIdentityConfirm" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="closeIdentityConfirm"></div>
        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Run Identity Verification?</h3>
            <p class="text-sm text-gray-600 mb-4">
              This runs an external identity check (OCR, IPRS, Hakikisha, face match) and incurs a cost.
              It can only be run <strong>once</strong> per ticket — this action cannot be undone or repeated.
            </p>
            <div class="flex justify-end space-x-3">
              <button
                type="button"
                @click="closeIdentityConfirm"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                @click="confirmRunIdentityVerification"
                :disabled="isRunningIdentityCheck"
                class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700"
              >
                {{ isRunningIdentityCheck ? "Running..." : "Yes, Run Check" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Complete Verification Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="closeModal"></div>
        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Final Verification Decision</h3>
            <form @submit.prevent="submit">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Stage</label>
                <select v-model="form.final_stage" required class="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select stage</option>
                  <option :value="1">Credit Score / Fraud</option>
                  <option :value="2">SMS</option>
                  <option :value="3">KYC</option>
                  <option :value="4">Veriscore Final</option>
                </select>
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Decision</label>
                <select v-model="form.decision" required class="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select decision</option>
                  <option :value="1">Approve</option>
                  <option :value="0">Reject</option>
                </select>
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Summary</label>
                <textarea v-model="form.summary" required rows="4" class="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
              </div>
              <div class="mt-6 flex justify-end space-x-3">
                <button type="button" @click="closeModal" class="px-4 py-2 border border-red-400 text-red-400 rounded-lg">Cancel</button>
                <button type="submit" :disabled="isSubmitting" class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700">
                  {{ isSubmitting ? "Submitting..." : "Submit" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import api from "@/utils/api";
import { useNotifications } from "@/composables/useNotifications";

const route = useRoute();
const router = useRouter();
const { showError, formatValidationErrors } = useNotifications();

const verifications = ref([]);
const showModal = ref(false);
const isSubmitting = ref(false);
const form = ref({ final_stage: "", decision: "", summary: "" });

const completeness = ref({ complete: false, incomplete: [], has_rejection: false, stages: [] });

const identityVerification = ref(null);
const isRunningIdentityCheck = ref(false);
const identityConfirmed = ref(false);
const showIdentityConfirm = ref(false);

// A rejection recorded on this ticket's Final Verification history means the
// page is done — no further identity checks or submissions should happen.
const ticketRejected = computed(() =>
  verifications.value.some((v) => v.decision === 0)
);

const stageLabel = (status) => {
  if (status === "rejected") return "Rejected — Cannot Proceed";
  if (status === "incomplete") return "Not Started";
  return "Approved";
};

const stageBadgeClass = (status) => {
  if (status === "rejected") return "bg-red-100 text-red-700";
  if (status === "incomplete") return "bg-gray-100 text-gray-500";
  return "bg-green-100 text-green-700";
};

const goBack = () => router.push(`/admin/tickets/${route.params.id}`);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

const fetchVerifications = async () => {
  try {
    const response = await api.get("/api/v2/final-verifications", {
      params: { ticket_id: route.params.id },
    });
    console.log(response.data);
    verifications.value = response.data;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load final verifications", details, 5000);
  }
};

const fetchCompleteness = async () => {
  try {
    const response = await api.get(`/api/v2/final-verifications/status/${route.params.id}`);
    completeness.value = response.data;
    console.log(response.data);
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load verification status", details, 5000);
  }
};

const fetchIdentityVerification = async () => {
  try {
    const response = await api.get(`/api/v2/kyc-identity-verifications/${route.params.id}`);
    const data = response.data;
    // Treat null, undefined, or an empty object as "no record yet"
    identityVerification.value = data && Object.keys(data).length > 0 ? data : null;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load identity verification", details, 5000);
  }
};

const openIdentityConfirm = () => {
  showIdentityConfirm.value = true;
};

const closeIdentityConfirm = () => {
  showIdentityConfirm.value = false;
};

const runIdentityVerification = async () => {
  isRunningIdentityCheck.value = true;
  try {
    const response = await api.post("/api/v2/kyc-identity-verifications", {
      ticket_id: route.params.id,
    });
    identityVerification.value = response.data;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to run identity verification", details, 5000);
  } finally {
    isRunningIdentityCheck.value = false;
  }
};

const confirmRunIdentityVerification = async () => {
  showIdentityConfirm.value = false;
  await runIdentityVerification();
};

const openModal = () => {
  form.value = { final_stage: "", decision: "", summary: "" };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const submit = async () => {
  isSubmitting.value = true;
  try {
    await api.post("/api/v2/final-verifications", {
      ...form.value,
      ticket_id: route.params.id,
      client_id: route.query.client_id,
    });
    closeModal();
    fetchVerifications();
    fetchCompleteness();
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to submit final verification", details, 5000);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  fetchVerifications();
  fetchCompleteness();
  fetchIdentityVerification();
});
</script>